import queryString from 'query-string';
import { axios, getAxiosAuthHeader, httpWithAuth } from '~/clients';
import {
  CANCEL_PREORDER,
  CANCEL_PURCHASE,
  CUSTOMER_JOURNEY_SERVICE,
  GET_OWNER_REVIEWS,
  GET_PURCHASE_DETAILS,
  GET_SOCIAL_PROOF,
  RECALL_STATUS,
  SUBSCRIBE_TO_VEHICLE_AVAILABILITY,
  USER_VEHICLE_AVAILABILITY
} from '~/constants/endpoints';
import type {
  AvailabilityResponse,
  Recall,
  SocialProof,
  UserFinancing
} from '~/contracts/vdp';
import { userFinancingQuery } from '~/queries';
import { BrowserCookies } from '~/utilities';
import { PurchasingResponse } from './contracts';

export const fetchUserVehicleAvailability = ({
  userId,
  vehicleId,
  zip: zipCode
}): Promise<AvailabilityResponse> => {
  const qs = queryString.stringify({ userId, vehicleId, zipCode });

  return axios
    .get<AvailabilityResponse>(`${USER_VEHICLE_AVAILABILITY}?${qs}`)
    .then(res => ({
      ...res.data,
      vehicleAvailableDateTime: res.data.vehicleAvailableDateTime
        ? res.data.vehicleAvailableDateTime.replace(/Z?$/i, 'Z') // Reference REO-2363 for when we can remove this
        : undefined
    }));
};

export const cancelPurchase = () =>
  httpWithAuth.post(CANCEL_PURCHASE, {
    reasons: ['CustomerSelfCancelled'],
    notes: '',
    executeReturnVehicle: false
  });

export const cancelPreOrder = ({ vehicleId, notes = '' }) =>
  httpWithAuth.post(CANCEL_PREORDER, {
    vehicleId,
    notes,
    reasons: ['CustomerSelfCancelled']
  });

export const fetchRecallStatus = (vehicleId: number) =>
  axios.get<Recall[]>(`${RECALL_STATUS}?vehicleId=${vehicleId}`);

export const subscribeToVehicleAvailability = (
  contactValue,
  contactType,
  vehicleId
) =>
  axios.post(`${SUBSCRIBE_TO_VEHICLE_AVAILABILITY}`, {
    contactValue,
    contactType,
    vehicleId,
    browserCookieId: BrowserCookies.get('BrowserCookieId') || null
  });

export const getSocialProof = (vehicleId: number) =>
  axios
    .get<SocialProof>(`${GET_SOCIAL_PROOF}?vehicleId=${vehicleId}`)
    .then(res => res.data);

export const fetchUserPurchasing = () =>
  httpWithAuth
    .get<PurchasingResponse>(GET_PURCHASE_DETAILS)
    .then(res => res.data);

export const fetchReviewsCount = (
  make: string,
  model: string,
  trim: string,
  year: number
) =>
  axios.post<{ summary: { review_count_total } }>(GET_OWNER_REVIEWS, {
    minimumReviews: 6,
    make,
    model,
    trim,
    year
  });

export const fetchUserFinancing = async (
  userId: string
): Promise<UserFinancing | null> =>
  httpWithAuth
    .post<{ data: { aCustomer: { financing: UserFinancing | null } } }>(
      CUSTOMER_JOURNEY_SERVICE,
      {
        query: userFinancingQuery,
        variables: {
          userId
        }
      },
      {
        headers: {
          ...getAxiosAuthHeader(),
          'apollographql-client-name': process.env.npm_package_name ?? '',
          'apollographql-client-version': process.env.npm_package_version ?? ''
        }
      }
    )
    .then(res => res.data.data.aCustomer.financing);
